import React, { useCallback, useEffect, useState } from "react";
import "./FyersAccessToken.css";
import { environment } from "environment/environment";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const FYERS_ACCOUNT_NAMES = ["NK", "PK", "BH", "GH"];
const DHAN_ACCOUNT_NAMES = ["PK", "BH"];
const INDEX_API_ACCOUNTS = new Set(["NK", "PK"]);

const INDEX_API_BASE_URL = environment.baseUrl;
const STOCK_API_BASE_URL = environment.stockApiBaseUrl;
const DHAN_TOKEN_BASE_URL = `${STOCK_API_BASE_URL}/dhan-api-token`;

const getResponseRows = (response) => {
    const data = response?.data ?? response;

    if (Array.isArray(data)) return data;
    if (data && typeof data === "object") return [data];
    return [];
};

const getFyersApiConfig = (accountName) => {
    if (INDEX_API_ACCOUNTS.has(accountName)) {
        return {
            apiName: "INDEX_API",
            getTokensUrl: `${INDEX_API_BASE_URL}/index-api-fyersone-login/get-all-index-api-fyers-token`,
            loginUrl: `${INDEX_API_BASE_URL}/index-api-fyersone-login/login`
        };
    }

    return {
        apiName: "STOCK_API",
        getTokensUrl: `${STOCK_API_BASE_URL}/stock-api-fyersone-login/get-all-stock-api-fyers-token`,
        loginUrl: `${STOCK_API_BASE_URL}/stock-api-fyersone-login/login`
    };
};

export default function FyersAccesstoken() {
    const [activeBroker, setActiveBroker] = useState("FYERS");
    const [tokensByAccount, setTokensByAccount] = useState(() =>
        Object.fromEntries(FYERS_ACCOUNT_NAMES.map(accountName => [accountName, []]))
    );
    const [generatingAccounts, setGeneratingAccounts] = useState(() =>
        Object.fromEntries(FYERS_ACCOUNT_NAMES.map(accountName => [accountName, false]))
    );
    const [dhanTokensByAccount, setDhanTokensByAccount] = useState(() =>
        Object.fromEntries(DHAN_ACCOUNT_NAMES.map(accountName => [accountName, []]))
    );
    const [generatingDhanAccounts, setGeneratingDhanAccounts] = useState(() =>
        Object.fromEntries(DHAN_ACCOUNT_NAMES.map(accountName => [accountName, false]))
    );
    const brokerTokenCounts = {
        FYERS: Object.values(tokensByAccount).reduce((count, tokens) => count + tokens.length, 0),
        DHAN: Object.values(dhanTokensByAccount).reduce((count, tokens) => count + tokens.length, 0)
    };

    /* ================= FYERS TOKENS ================= */
    const fetchFyersTokens = useCallback(async (selectedAccountName) => {
        try {
            const fetchTokensByAccount = (accountName) => {
                const { apiName, getTokensUrl } = getFyersApiConfig(accountName);

                return fetch(
                    `${getTokensUrl}?apiName=${apiName}&accountName=${accountName}`
                ).then(response => {
                    if (!response.ok) {
                        throw new Error(`Token request failed with status ${response.status}`);
                    }
                    return response.json();
                });
            };

            const filterTokensByAccount = (response, accountName) => {
                if (!response.status || !Array.isArray(response.data)) return [];

                return response.data.filter(
                    row => String(row.accountName ?? "").trim().toUpperCase() === accountName
                ).slice(0, 2);
            };

            const accountNames = selectedAccountName
                ? [selectedAccountName]
                : FYERS_ACCOUNT_NAMES;

            const accountResponses = await Promise.all(
                accountNames.map(async accountName => ({
                    accountName,
                    response: await fetchTokensByAccount(accountName)
                }))
            );

            setTokensByAccount(previous => {
                const updatedTokens = { ...previous };

                accountResponses.forEach(({ accountName, response }) => {
                    updatedTokens[accountName] = filterTokensByAccount(response, accountName);
                });

                return updatedTokens;
            });
        } catch {
            toast.error("Failed to load Fyers API tokens");
        }
    }, []);

    useEffect(() => {
        fetchFyersTokens();
    }, [fetchFyersTokens]);

    const generateAccountToken = async (accountName) => {
        const { apiName, loginUrl } = getFyersApiConfig(accountName);

        setGeneratingAccounts(previous => ({
            ...previous,
            [accountName]: true
        }));
        try {
            const response = await fetch(`${loginUrl}?accountName=${accountName}`);
            if (!response.ok) {
                throw new Error(`Token generation failed with status ${response.status}`);
            }
            const res = await response.json();

            if (res.status) {
                toast.success(`${accountName} ${apiName} token generated`);
                await fetchFyersTokens();
            }
        } catch {
            toast.error(`${accountName} ${apiName} generation failed`);
        } finally {
            setGeneratingAccounts(previous => ({
                ...previous,
                [accountName]: false
            }));
        }
    };

    const fetchDhanTokens = useCallback(async (selectedAccountName) => {
        try {
            const accountNames = selectedAccountName
                ? [selectedAccountName]
                : DHAN_ACCOUNT_NAMES;
            const accountResponses = await Promise.all(
                accountNames.map(async accountName => {
                    const response = await fetch(
                        `${DHAN_TOKEN_BASE_URL}/get-dhan-token?accountName=${accountName}`
                    ).then(r => r.json());

                    return { accountName, response };
                })
            );

            setDhanTokensByAccount(previous => {
                const updatedTokens = { ...previous };

                accountResponses.forEach(({ accountName, response }) => {
                    updatedTokens[accountName] = getResponseRows(response).filter(row =>
                        !row?.accountName ||
                        String(row.accountName).trim().toUpperCase() === accountName
                    );
                });

                return updatedTokens;
            });
        } catch {
            toast.error("Failed to load DHAN API tokens");
        }
    }, []);

    useEffect(() => {
        if (activeBroker === "DHAN") fetchDhanTokens();
    }, [activeBroker, fetchDhanTokens]);

    const generateDhanToken = async (accountName) => {
        setGeneratingDhanAccounts(previous => ({ ...previous, [accountName]: true }));

        try {
            const response = await fetch(
                `${DHAN_TOKEN_BASE_URL}/generate-dhan-token?accountName=${accountName}`,
                { method: "POST" }
            ).then(r => r.json());

            if (response?.status === false) {
                throw new Error(response?.message);
            }

            toast.success(`${accountName} DHAN token generated`);
            await fetchDhanTokens(accountName);
        } catch {
            toast.error(`${accountName} DHAN token generation failed`);
        } finally {
            setGeneratingDhanAccounts(previous => ({ ...previous, [accountName]: false }));
        }
    };

    /* ================= TABLE ================= */
    const renderTable = (data) => (
        <table className="responsive-table fyers-token-table table-bordered text-center sticky-header full-height-table">
            <colgroup>
                <col className="date-column" />
                <col className="time-column" />
                <col className="generated-by-column" />
                <col className="api-name-column" />
                <col className="account-name-column" />
            </colgroup>
            <thead>
                <tr>
                    <th>DATE</th>
                    <th>TIME</th>
                    <th>GENERATED BY</th>
                    <th>API NAME</th>
                    <th>ACCOUNT NAME</th>
                </tr>
            </thead>
            <tbody>
                {data.length ? (
                    data.map((row, i) => (
                        <tr key={i}>
                            <td data-label="DATE">{row.createdDate ?? "--"}</td>
                            <td data-label="TIME">{row.createdTime ?? "--"}</td>
                            <td data-label="GENERATED BY">{row.generatedBy ?? "--"}</td>
                            <td data-label="API NAME">{row.apiName ?? "--"}</td>
                            <td data-label="ACCOUNT NAME">{row.accountName ?? "--"}</td>
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td className="empty-state" colSpan="5">No data available</td>
                    </tr>
                )}
            </tbody>
        </table>
    );

    const renderDhanTable = (data) => (
        <table className="responsive-table dhan-token-table table-bordered text-center sticky-header full-height-table">
            <colgroup>
                <col className="date-column" />
                <col className="time-column" />
                <col className="generated-by-column" />
                <col className="account-name-column" />
            </colgroup>
            <thead>
                <tr>
                    <th>DATE</th>
                    <th>TIME</th>
                    <th>GENERATED BY</th>
                    <th>ACCOUNT NAME</th>
                </tr>
            </thead>
            <tbody>
                {data.length ? data.map((row, index) => (
                    <tr key={row.id ?? index}>
                        <td data-label="DATE">{row.createdDate ?? row.date ?? "--"}</td>
                        <td data-label="TIME">{row.createdTime ?? row.time ?? "--"}</td>
                        <td data-label="GENERATED BY">{row.generatedBy ?? "--"}</td>
                        <td data-label="ACCOUNT NAME">{row.accountName ?? "--"}</td>
                    </tr>
                )) : (
                    <tr><td className="empty-state" colSpan="4">No data available</td></tr>
                )}
            </tbody>
        </table>
    );

    return (
        <div className="fyers-access-token-page full-page">
            <ToastContainer position="top-center" />

            {/* ===== HEADER (UNCHANGED) ===== */}
            <header className="header">
                <nav className="nav-container">

                    {/* LEFT SIDE - LOGO */}
                    <div className="left-nav">
                        <div className="logo-container">
                            <img
                                src="/assets/images/noptionwave-logo-1.png"
                                className="header-logo"
                                alt="noptionwave logo"
                            />
                        </div>
                    </div>

                </nav>
            </header>

            <div className="broker-tabs" role="tablist" aria-label="Broker">
                {["FYERS", "DHAN"].map(broker => (
                    <button
                        key={broker}
                        type="button"
                        role="tab"
                        aria-selected={activeBroker === broker}
                        className={`broker-tab ${activeBroker === broker ? "active" : ""}`}
                        onClick={() => setActiveBroker(broker)}
                    >
                        {broker} ({brokerTokenCounts[broker]})
                    </button>
                ))}
            </div>

            <div className="account-content full-page-content">
                <div className="table-container full-page-table">
                    <div className="index-account-tables">
                        {(activeBroker === "FYERS" ? FYERS_ACCOUNT_NAMES : DHAN_ACCOUNT_NAMES).map(accountName => (
                            <section className="account-table-section" key={accountName}>
                                <div className="account-table-header">
                                    <h4 className="account-table-title">
                                        {accountName} Account ({activeBroker === "FYERS"
                                            ? tokensByAccount[accountName].length
                                            : dhanTokensByAccount[accountName].length})
                                    </h4>
                                    <button
                                        type="button"
                                        className="generate-token-btn"
                                        aria-label={`Generate Token ${accountName}`}
                                        onClick={() => activeBroker === "FYERS"
                                            ? generateAccountToken(accountName)
                                            : generateDhanToken(accountName)}
                                        disabled={activeBroker === "FYERS"
                                            ? generatingAccounts[accountName]
                                            : generatingDhanAccounts[accountName]}
                                    >
                                        {(activeBroker === "FYERS"
                                            ? generatingAccounts[accountName]
                                            : generatingDhanAccounts[accountName])
                                            ? `Generating ${accountName}...`
                                            : `Generate Token ${accountName}`}
                                    </button>
                                </div>
                                {activeBroker === "FYERS"
                                    ? renderTable(tokensByAccount[accountName])
                                    : renderDhanTable(dhanTokensByAccount[accountName])}
                            </section>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
